import getProductKey from 'helper/requests/getProductKey';
import {
  PainelOptions,
  Entidade,
  EntidadesListParams,
  ChartRequestSH,
  ChartResponseSH,
  ChartRequestSM,
  ChartResponseSM,
  ComparativoSMRequest,
  ComparativoSMData,
  ModelosDisponiveisResponse,
  TipoRodada,
  WindxTela,
  WindxPreferencia,
  SalvarPreferenciaRequest,
  WindxSimulacaoAntiga,
  SalvarSimulacaoAntigaRequest,
  UsinasEolicasResponse,
  DownloadZipRequest,
  DownloadZipResponseData,
  ConsultaEstimativaRequest,
  EstimativaCusto,
} from 'helper/windx';
import { InstructionTab } from 'types/Windx/About/getInstructionTabsResponse';
import GetInstructionPdfParams from 'types/Windx/About/getInstructionPdfParams';
import { transformInstructionTabsResponse, transformInstructionPdfResponse } from 'helper/windx/about';
import baseApi from './base';

/**
 * Service RTK Query do modulo Windx.
 *
 * Padrao seguido: src/services/api/studies.tsx
 * Design doc: docs/superpowers/specs/2026-04-08-fe-windx-002-cliente-http-design.md
 * Contratos JSON: windx-docs/windx/TEMPLATE_ISSUES_WINDX.md secao 5
 *
 * Inclui mutation `triggerDownloadZip` (substituiu o CSV em 2026-05-14):
 * retorna PAR URL do bucket OCI. Cada chamada baixa 1 ZIP de uma rodada
 * especifica (1 modelo). O consumidor (BotaoDownload) chama N vezes em
 * paralelo via Promise.all quando o usuario tem N modelos selecionados.
 */

interface GetModelosDisponiveisParams {
  tela: WindxTela;
  data_simulacao?: string;
  // Filtros opcionais v1.1 do contrato: alinham disponibilidade com o que
  // chart/data vai consultar. com_rmv aplica em ambas as telas.
  // Sem esses filtros, BE cai no fallback v1.0 (so id_modelo+data_rodada),
  // e modelos podem aparecer disponivel=true mas sem dados para a
  // combinacao tipo_rodada/com_rmv selecionada no painel.
  // WEOL-SM (#99): tipo_rodada vale em sh E sm. No SM a fato semanal ganhou a
  // coluna e o BE devolve, por modelo, a ultima rodada DAQUELE tipo (omitido =
  // 'final'); no SH, omitido = qualquer tipo.
  tipo_rodada?: TipoRodada;
  com_rmv?: boolean;
}

// Helper local: extrai `data` do envelope { status, code, data, ... }
// que todas as rotas do backend retornam.
const extractData = <T extends object>(response: { data: T }): T => response.data;

export const windxApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    // 5.1 GET /produtos/windx/painel-options/?tela=X
    getPainelOptions: build.query<PainelOptions, WindxTela>({
      query: (tela) => {
        const productKey = getProductKey('windx');
        return {
          url: `produtos/windx/painel-options/?product_key=${productKey}&tela=${tela}`,
        };
      },
      transformResponse: extractData,
      providesTags: ['WindxPainelOptions'],
    }),

    // 5.2 GET /produtos/windx/entidades/list/?subdivisao=X[&tela=Y[&data_simulacao=Z]]
    // Aceita tanto `number` (backward-compat — call site antigo passa só subdivisao)
    // quanto `EntidadesListParams` (contrato v1.1 — tela/data_simulacao para
    // calcular `disponivel` por entidade).
    getEntidades: build.query<Entidade[], number | EntidadesListParams>({
      query: (arg) => {
        const productKey = getProductKey('windx');
        const params = typeof arg === 'number' ? { subdivisao: arg } : arg;
        const qs = new URLSearchParams({
          product_key: productKey,
          subdivisao: String(params.subdivisao),
          ...(params.tela ? { tela: params.tela } : {}),
          ...(params.data_simulacao ? { data_simulacao: params.data_simulacao } : {}),
        });
        return { url: `produtos/windx/entidades/list/?${qs.toString()}` };
      },
      transformResponse: extractData,
      providesTags: ['WindxEntidades'],
    }),

    // 5.3 GET /produtos/windx/modelos-disponiveis/?tela=X&data_simulacao=Y
    //                                              &tipo_rodada=Z&com_rmv=W
    getModelosDisponiveis: build.query<ModelosDisponiveisResponse, GetModelosDisponiveisParams>({
      query: ({
        tela,
        data_simulacao: dataSimulacao,
        tipo_rodada: tipoRodada,
        com_rmv: comRmv,
      }) => {
        const qs = new URLSearchParams({
          product_key: getProductKey('windx'),
          tela,
        });
        if (dataSimulacao) qs.set('data_simulacao', dataSimulacao);
        // WEOL-SM (#99): repassa o tipo em qualquer tela (antes so em sh).
        if (tipoRodada) qs.set('tipo_rodada', tipoRodada);
        if (comRmv !== undefined) qs.set('com_rmv', comRmv ? 'true' : 'false');
        return { url: `produtos/windx/modelos-disponiveis/?${qs.toString()}` };
      },
      transformResponse: extractData,
      providesTags: ['WindxModelosDisponiveis'],
    }),

    // 5.4 POST /produtos/windx/chart/data/ (build.query com POST para cache automatico)
    // product_key vai na query string (mesmo padrao das rotas 5.1/5.2/5.3) — o
    // backend exige; sem ele o auth.has_permission() devolve 403 "Chave do
    // Produto Ausente".
    getChartDataSH: build.query<ChartResponseSH, ChartRequestSH>({
      query: (payload) => ({
        url: `produtos/windx/chart/data/?product_key=${getProductKey('windx')}`,
        method: 'POST',
        body: payload,
      }),
      transformResponse: extractData,
      providesTags: ['WindxChartDataSH'],
      // Trocar Semi-horario <-> Diario reconsulta com preferCacheValue
      // (useChartDataMergedSH): 10 min de cache deixa o usuario voltar para a
      // sub-aba ja consultada e ver o resultado sem novo request.
      keepUnusedDataFor: 600,
    }),

    // 5.16 POST /produtos/windx/consulta/estimativa/ (WINDX-051) — pré-voo barato.
    // Dado a seleção, devolve a estimativa de custo (séries/pontos/payload/
    // veredito/sugestões) SEM rodar a query pesada. Consumido pelo medidor de
    // complexidade (debounce ~300ms via useLazy) para bloquear consultas
    // inviáveis ANTES do envio. Contrato: contratos/common/consulta-estimativa.json.
    // Sem providesTags: leitura efêmera, re-disparada a cada mudança de seleção.
    getConsultaEstimativa: build.query<EstimativaCusto, ConsultaEstimativaRequest>({
      query: (payload) => ({
        url: `produtos/windx/consulta/estimativa/?product_key=${getProductKey('windx')}`,
        method: 'POST',
        body: payload,
      }),
      transformResponse: extractData,
    }),

    // 5.5 POST /produtos/windx/sm/chart/data/ (build.query com POST para cache automatico)
    getChartDataSM: build.query<ChartResponseSM, ChartRequestSM>({
      query: (payload) => ({
        url: `produtos/windx/sm/chart/data/?product_key=${getProductKey('windx')}`,
        method: 'POST',
        body: payload,
      }),
      transformResponse: extractData,
      providesTags: ['WindxChartDataSM'],
    }),

    // 5.6 POST /produtos/windx/sm/comparativo/ (WINDX-016 / FE-WINDX-007)
    // Tabela pivot de convergencia das previsoes semanais — backend devolve
    // estrutura JSON pronta (nao ECharts). Frontend renderiza tabela.
    getComparativoSM: build.query<ComparativoSMData, ComparativoSMRequest>({
      query: (payload) => ({
        url: `produtos/windx/sm/comparativo/?product_key=${getProductKey('windx')}`,
        method: 'POST',
        body: payload,
      }),
      transformResponse: extractData,
      providesTags: ['WindxComparativoSM'],
    }),

    // 5.9 GET /produtos/windx/preferencias/list/ (FE-WINDX-008)
    getPreferencias: build.query<WindxPreferencia[], void>({
      query: () => ({
        url: `produtos/windx/preferencias/list/?product_key=${getProductKey('windx')}`,
      }),
      transformResponse: extractData,
      providesTags: ['WindxPreferencias'],
    }),

    // 5.10 POST /produtos/windx/preferencias/save/ (FE-WINDX-008)
    savePreferencia: build.mutation<{ id: number }, SalvarPreferenciaRequest>({
      query: (payload) => ({
        url: `produtos/windx/preferencias/save/?product_key=${getProductKey('windx')}`,
        method: 'POST',
        body: payload,
      }),
      transformResponse: extractData,
      invalidatesTags: ['WindxPreferencias'],
    }),

    // 5.11 DELETE /produtos/windx/preferencias/delete/ (FE-WINDX-008)
    deletePreferencia: build.mutation<void, number>({
      query: (id) => ({
        url: `produtos/windx/preferencias/delete/?product_key=${getProductKey('windx')}`,
        method: 'DELETE',
        body: { id },
      }),
      invalidatesTags: ['WindxPreferencias'],
    }),

    // 5.13 GET /produtos/windx/simulacoes-antigas/list/ (FE-WINDX-043)
    // Lista simulacoes antigas salvas pelo usuario para WEOL-SH. Backend
    // retorna `dias_atras` (offset relativo 1..14); o FE resolve para data
    // absoluta apenas no momento de chamar /chart/data/.
    // Contrato: contratos/sh/simulacoes-antigas.json v1.0
    getSimulacoesAntigas: build.query<WindxSimulacaoAntiga[], void>({
      query: () => ({
        url: `produtos/windx/simulacoes-antigas/list/?product_key=${getProductKey('windx')}&tela=sh`,
      }),
      transformResponse: extractData,
      providesTags: ['WindxSimulacoesAntigas'],
    }),

    // 5.14 POST /produtos/windx/simulacoes-antigas/save/ (FE-WINDX-043)
    // Upsert por (user_id, nome). Re-save com mesmo nome ATUALIZA o registro.
    saveSimulacaoAntiga: build.mutation<{ id: number }, SalvarSimulacaoAntigaRequest>({
      query: (payload) => ({
        url: `produtos/windx/simulacoes-antigas/save/?product_key=${getProductKey('windx')}`,
        method: 'POST',
        body: payload,
      }),
      transformResponse: extractData,
      invalidatesTags: ['WindxSimulacoesAntigas'],
    }),

    // 5.15 DELETE /produtos/windx/simulacoes-antigas/delete/ (FE-WINDX-043)
    deleteSimulacaoAntiga: build.mutation<void, number>({
      query: (id) => ({
        url: `produtos/windx/simulacoes-antigas/delete/?product_key=${getProductKey('windx')}`,
        method: 'DELETE',
        body: { id },
      }),
      invalidatesTags: ['WindxSimulacoesAntigas'],
    }),

    // 5.8 POST /produtos/windx/download/zip/ — gera PAR URL do bucket OCI windx
    // (substituiu o CSV de banco em 2026-05-14). 1 chamada = 1 ZIP por tupla
    // (tipo, data_rodada, modelo, rmv, tipo_rodada). FE chama N vezes para N
    // modelos selecionados.
    triggerDownloadZip: build.mutation<DownloadZipResponseData, DownloadZipRequest>({
      query: (payload) => ({
        url: `produtos/windx/download/zip/?product_key=${getProductKey('windx')}`,
        method: 'POST',
        body: payload,
      }),
      transformResponse: extractData,
    }),

    // 5.12 GET /produtos/windx/usinas-eolicas/ (WINDX-021 / FE-WINDX-011)
    // Single-fetch das ~1052 usinas com 15 campos denormalizados. Consumido
    // pelo Modal SeletorEntidades quando subdivisao=4 (usinas) — frontend
    // faz filtro client-side em vez de paginar via /entidades/list/.
    //
    // transformResponse aqui devolve { data, meta } em vez de soh data —
    // o consumidor precisa do `meta.etag_dados` para revalidacao via
    // If-None-Match (futuro) e do `meta.total` para exibir contagem.
    getUsinasEolicas: build.query<UsinasEolicasResponse, void>({
      query: () => ({
        url: `produtos/windx/usinas-eolicas/?product_key=${getProductKey('windx')}`,
      }),
      transformResponse: (response: UsinasEolicasResponse & { data: unknown }) => ({
        data: response.data as UsinasEolicasResponse['data'],
        meta: response.meta,
      }),
      providesTags: ['WindxUsinasEolicas'],
    }),

    // 5.13 GET /produtos/windx/about/ (WINDX-044 / FE-WINDX-047)
    // Retorna { id: titulo } das abas ativas (formato igual ao Flux About).
    getWindxAboutTabs: build.query<InstructionTab[], void>({
      query: () => ({
        url: 'produtos/windx/about/',
        method: 'GET',
      }),
      transformResponse: transformInstructionTabsResponse,
    }),

    // 5.14 POST /produtos/windx/about/pdf/ (WINDX-044 / FE-WINDX-047)
    // Retorna URL assinada (PAR 24h) do PDF da aba. Body: { id }.
    getWindxAboutPdf: build.query<string, GetInstructionPdfParams>({
      query: ({ id }) => ({
        url: 'produtos/windx/about/pdf/',
        method: 'POST',
        body: { id },
      }),
      transformResponse: transformInstructionPdfResponse,
    }),
  }),
});

export const {
  useGetPainelOptionsQuery,
  useGetEntidadesQuery,
  useGetModelosDisponiveisQuery,
  useGetChartDataSHQuery,
  useGetChartDataSMQuery,
  useLazyGetConsultaEstimativaQuery,
  useGetComparativoSMQuery,
  useLazyGetChartDataSHQuery,
  useLazyGetChartDataSMQuery,
  useLazyGetComparativoSMQuery,
  useGetPreferenciasQuery,
  useSavePreferenciaMutation,
  useDeletePreferenciaMutation,
  useGetSimulacoesAntigasQuery,
  useSaveSimulacaoAntigaMutation,
  useDeleteSimulacaoAntigaMutation,
  useGetUsinasEolicasQuery,
  useLazyGetUsinasEolicasQuery,
  useTriggerDownloadZipMutation,
  useGetWindxAboutTabsQuery,
  useLazyGetWindxAboutPdfQuery,
} = windxApi;
